import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PhoneIcon, MenuIcon, XIcon, ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { FIRM, NAV, NavItem } from '../../lib/constants';
import { Button } from '../ui/Button';
import { Container } from '../ui/Container';
import { cn } from '../../lib/utils';
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [openMega, setOpenMega] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    setMobileOpen(false);
    setOpenMega(null);
  }, [location]);
  return <header className={cn('sticky top-0 z-50 w-full transition-all duration-300', scrolled ? 'bg-cream/95 shadow-[0_1px_0_rgba(15,42,67,0.08)] backdrop-blur' : 'bg-cream')} onMouseLeave={() => setOpenMega(null)} data-id="element-271">
      {/* top utility strip — brand green (matches cullenmacleod.com.au) */}
      <div className="hidden bg-brass text-white lg:block" data-id="element-272">
        <Container className="flex h-9 items-center justify-between text-xs" data-id="element-273">
          <p className="tracking-wide text-white/90" data-id="element-274">
            {FIRM.addressLine1}, {FIRM.addressLine2}
          </p>
          <div className="flex items-center gap-5 text-white/90" data-id="element-275">
            <a href={FIRM.emailHref} className="transition-colors hover:text-white" data-id="element-276">
              {FIRM.email}
            </a>
            <span className="text-white/40" data-id="element-277">|</span>
            <span className="text-white/75" data-id="element-278">
              Serving Perth & Western Australia
            </span>
          </div>
        </Container>
      </div>

      <Container className="flex h-[68px] items-center justify-between lg:h-[76px]" data-id="element-279">
        <Link to="/" className="flex items-center" aria-label={`${FIRM.name} home`} data-id="element-280">
          <img src={FIRM.logo} alt={FIRM.name} className="h-8 w-auto lg:h-9" data-id="element-281" />
        </Link>

        {/* desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary" data-id="element-282">
          {NAV.map(item => <NavLink key={item.label} item={item} openMega={openMega} setOpenMega={setOpenMega} data-id="element-283" />)}
        </nav>

        <div className="hidden items-center gap-2.5 lg:flex" data-id="element-284">
          <Button href={FIRM.phoneHref} variant="outline" size="sm" data-id="element-285">
            <PhoneIcon className="h-4 w-4" data-id="element-286" />
            Call {FIRM.phoneDisplay}
          </Button>
          <Button to="/enquiry" variant="primary" size="sm" data-id="element-287">
            Make an Enquiry
          </Button>
        </div>

        {/* mobile toggle */}
        <button className="flex h-10 w-10 items-center justify-center rounded-md text-navy lg:hidden" onClick={() => setMobileOpen(v => !v)} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen} data-id="element-288">
          {mobileOpen ? <XIcon className="h-6 w-6" data-id="element-289" /> : <MenuIcon className="h-6 w-6" data-id="element-290" />}
        </button>
      </Container>

      {/* mega menu panel */}
      <AnimatePresence data-id="element-291">
        {openMega && <MegaPanel item={NAV.find(n => n.label === openMega)!} onClose={() => setOpenMega(null)} data-id="element-292" />}
      </AnimatePresence>

      {/* mobile drawer */}
      <AnimatePresence data-id="element-293">
        {mobileOpen && <MobileNav onClose={() => setMobileOpen(false)} data-id="element-294" />}
      </AnimatePresence>
    </header>;
}
function NavLink({
  item,
  openMega,
  setOpenMega
}: {
  item: NavItem;
  openMega: string | null;
  setOpenMega: (v: string | null) => void;
}) {
  const hasMega = !!item.columns;
  const active = openMega === item.label;
  const isRoute = item.href.startsWith('/') && !item.href.includes('#');
  const content = <span className="inline-flex items-center gap-1" data-id="element-295">
      {item.label}
      {hasMega && <ChevronDownIcon className={cn('h-3.5 w-3.5 transition-transform', active && 'rotate-180')} data-id="element-296" />}
    </span>;
  const cls = cn('rounded-md px-3 py-2 text-[0.92rem] font-medium text-navy/85 transition-colors hover:text-navy', active && 'text-navy');
  return <div onMouseEnter={() => setOpenMega(hasMega ? item.label : null)} data-id="element-297">
      {isRoute ? <Link to={item.href} className={cls} data-id="element-298">
          {content}
        </Link> : <a href={item.href} className={cls} data-id="element-299">
          {content}
        </a>}
    </div>;
}
function MegaPanel({
  item,
  onClose
}: {
  item: NavItem;
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: -8
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -8
  }} transition={{
    duration: 0.18,
    ease: 'easeOut'
  }} className="absolute left-0 right-0 top-full hidden border-t border-navy/10 bg-cream shadow-mega lg:block" onMouseEnter={() => {}} data-id="element-300">
      <Container className="grid grid-cols-12 gap-8 py-8" data-id="element-301">
        {item.intro && <div className="col-span-4 rounded-xl bg-navy p-7 text-cream" data-id="element-302">
            <h3 className="font-serif text-xl leading-snug" data-id="element-303">
              {item.intro.heading}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-cream/70" data-id="element-304">
              {item.intro.body}
            </p>
            <Link to="/enquiry" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brass-light hover:text-brass" onClick={onClose} data-id="element-305">
              {item.intro.cta}
              <ArrowRightIcon className="h-4 w-4" data-id="element-306" />
            </Link>
          </div>}
        <div className={cn('grid gap-8', item.intro ? 'col-span-8 grid-cols-2' : 'col-span-12 grid-cols-4')} data-id="element-307">
          {item.columns?.map(col => <div key={col.title} data-id="element-308">
              <Link to={col.href} onClick={onClose} className="mb-3 block font-serif text-[0.95rem] font-600 text-navy hover:text-brass-dark" data-id="element-309">
                {col.title}
              </Link>
              <ul className="space-y-1.5" data-id="element-310">
                {col.links.map(l => <li key={l.label} data-id="element-311">
                    <Link to={l.href} onClick={onClose} className="text-sm text-ink/65 transition-colors hover:text-navy" data-id="element-312">
                      {l.label}
                    </Link>
                  </li>)}
              </ul>
            </div>)}
        </div>
      </Container>
    </motion.div>;
}
function MobileNav({
  onClose
}: {
  onClose: () => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  return <motion.div initial={{
    opacity: 0,
    height: 0
  }} animate={{
    opacity: 1,
    height: 'auto'
  }} exit={{
    opacity: 0,
    height: 0
  }} transition={{
    duration: 0.22,
    ease: 'easeOut'
  }} className="overflow-hidden border-t border-navy/10 bg-cream lg:hidden" data-id="element-313">
      <Container className="py-4" data-id="element-314">
        <ul className="divide-y divide-navy/8" data-id="element-315">
          {NAV.map(item => {
          const hasMega = !!item.columns;
          const isRoute = item.href.startsWith('/') && !item.href.includes('#');
          return <li key={item.label} className="py-1" data-id="element-316">
                <div className="flex items-center justify-between" data-id="element-317">
                  {isRoute ? <Link to={item.href} onClick={onClose} className="py-2.5 font-medium text-navy" data-id="element-318">
                      {item.label}
                    </Link> : <a href={item.href} onClick={onClose} className="py-2.5 font-medium text-navy" data-id="element-319">
                      {item.label}
                    </a>}
                  {hasMega && <button onClick={() => setExpanded(v => v === item.label ? null : item.label)} aria-label={`Toggle ${item.label}`} className="p-2 text-navy/60" data-id="element-320">
                      <ChevronDownIcon className={cn('h-5 w-5 transition-transform', expanded === item.label && 'rotate-180')} data-id="element-321" />
                    </button>}
                </div>
                <AnimatePresence data-id="element-322">
                  {hasMega && expanded === item.label && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-323">
                      <div className="space-y-3 pb-3 pl-3" data-id="element-324">
                        {item.columns?.map(col => <div key={col.title} data-id="element-325">
                            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-brass-dark" data-id="element-326">
                              {col.title}
                            </p>
                            <ul className="space-y-1" data-id="element-327">
                              {col.links.map(l => <li key={l.label} data-id="element-328">
                                  <Link to={l.href} onClick={onClose} className="block py-1 text-sm text-ink/70" data-id="element-329">
                                    {l.label}
                                  </Link>
                                </li>)}
                            </ul>
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </li>;
        })}
        </ul>
        <div className="mt-5 flex flex-col gap-2.5" data-id="element-330">
          <Button href={FIRM.phoneHref} variant="secondary" size="md" data-id="element-331">
            <PhoneIcon className="h-4 w-4" data-id="element-332" /> Call {FIRM.phoneDisplay}
          </Button>
          <Button to="/enquiry" variant="primary" size="md" onClick={onClose} data-id="element-333">
            Make an Enquiry
          </Button>
        </div>
      </Container>
    </motion.div>;
}